import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Manual scroll-position management for SPA navigation:
 * - PUSH / REPLACE → scroll to top
 * - POP (back/forward) → restore the exact previous scroll position
 *
 * We store positions keyed by `location.key` so every history entry
 * gets its own saved offset.
 */
const scrollPositions = new Map<string, number>();

const ScrollToTop = () => {
  const { pathname, key } = useLocation();
  const navType = useNavigationType();
  const prevKey = useRef(key);

  // Persist current scroll position whenever we're about to leave.
  useEffect(() => {
    const save = () => {
      scrollPositions.set(prevKey.current, window.scrollY);
    };

    // Save on every scroll so the latest position is always captured.
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, []); // stable – only updates prevKey ref

  useEffect(() => {
    // Update the ref *after* we've used it for saving.
    prevKey.current = key;

    if (navType === "POP") {
      // Restore saved position (may need a short delay for the DOM to render).
      const saved = scrollPositions.get(key);
      if (saved !== undefined) {
        // requestAnimationFrame ensures the new page content is rendered first.
        requestAnimationFrame(() => {
          window.scrollTo(0, saved);
        });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, key, navType]);

  return null;
};

export default ScrollToTop;
